import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole, useSession } from "@/lib/session";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getPremiumAchievements, type LearnerAchievement } from "@/lib/premium-achievements.functions";
import { MedalCard } from "@/components/achievements/medal-card";
import { Certificate } from "@/components/achievements/certificate";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/learner/medals")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "Achievements & Medals — MAKAI" },
      { name: "description", content: "Elegant medals and certificates for consistency, mastery, and problem solving." },
    ],
  }),
  component: MedalsPage,
});

const CATEGORIES = [
  "All", "Consistency", "Learning Streaks", "Improvement", "Persistence",
  "Critical Thinking", "Perfect Working", "Reading", "Mathematics",
  "Science", "Creativity", "Problem Solving",
];

function MedalsPage() {
  const { session } = useSession();
  const fetchFn = useServerFn(getPremiumAchievements);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["premium-achievements"],
    queryFn: () => fetchFn({ data: {} }),
  });
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState<LearnerAchievement | null>(null);

  useEffect(() => {
    const t = setInterval(() => refetch(), 60_000);
    return () => clearInterval(t);
  }, [refetch]);

  const items = data?.items ?? [];
  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((i) => i.definition.category === filter)),
    [items, filter],
  );

  const earned = items.filter((i) => i.earned_at);
  const learnerName = session?.name || "Learner";

  return (
    <AppShell role="learner" title="Achievements" subtitle="Elegant medals for the habits that build a scholar.">
      <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-border bg-gradient-to-br from-[hsl(44_60%_96%)] via-card to-card p-5 dark:from-[hsl(44_20%_10%)]">
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-[hsl(38_75%_45%)] to-[hsl(52_100%_75%)] text-white shadow-lg">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <div className="font-serif text-2xl font-semibold tracking-tight">{earned.length} of {items.length} medals earned</div>
            <div className="text-sm text-muted-foreground">Each medal recognises a real, measurable habit — not participation.</div>
          </div>
        </div>
      </div>

      <div className="mb-4 flex gap-1.5 overflow-x-auto scrollbar-none">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilter(c)}
            className={cn(
              "whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
              filter === c
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:text-foreground",
            )}
          >
            {c}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-border bg-card p-12 text-sm text-muted-foreground">
          <Loader2 className="mb-2 h-5 w-5 animate-spin" /> Loading your achievements…
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((it) => (
            <MedalCard
              key={it.code}
              code={it.code}
              tier={it.tier}
              title={it.definition.title}
              description={it.definition.description}
              icon={it.definition.icon}
              category={it.definition.category}
              progress={it.progress}
              target={it.target}
              earned_at={it.earned_at}
              onOpen={() => it.earned_at && setOpen(it)}
            />
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-4xl print:max-w-none print:border-0 print:p-0 print:shadow-none">
          {open && (
            <Certificate
              learnerName={learnerName}
              title={open.definition.title}
              description={open.definition.description}
              category={open.definition.category}
              tier={open.tier}
              icon={open.definition.icon}
              serial={open.certificate_serial}
              earnedAt={open.earned_at}
            />
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
